import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import Button from './Button';

export interface StepDef {
  label: string;
  description?: string;
}

export interface StepperProps {
  steps: StepDef[];
  activeStep: number;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

function Stepper({ steps, activeStep, orientation = 'vertical', className }: StepperProps) {
  const isVertical = orientation === 'vertical';

  return (
    <nav
      aria-label="Progress"
      className={cn(
        isVertical ? 'flex flex-col' : 'flex items-center',
        className
      )}
    >
      {steps.map((step, index) => {
        const status = index < activeStep ? 'completed' : index === activeStep ? 'active' : 'upcoming';
        const isLast = index === steps.length - 1;

        return (
          <div
            key={index}
            className={cn(
              isVertical ? 'flex gap-3' : 'flex flex-col items-center',
              !isLast && !isVertical && 'flex-1'
            )}
          >
            <div className={cn(isVertical ? 'flex flex-col items-center' : 'flex items-center w-full')}>
              {/* Step circle */}
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors',
                  status === 'completed' && 'bg-primary text-primary-foreground',
                  status === 'active' && 'border-2 border-primary bg-card text-primary ring-2 ring-primary/20',
                  status === 'upcoming' && 'bg-muted text-muted-foreground'
                )}
                aria-current={status === 'active' ? 'step' : undefined}
              >
                {status === 'completed' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </div>

              {/* Horizontal connector */}
              {!isLast && !isVertical && (
                <div
                  className={cn(
                    'mx-2 h-0.5 flex-1',
                    index < activeStep ? 'bg-primary' : 'bg-border'
                  )}
                />
              )}
            </div>

            <div className={cn(isVertical && 'flex-1 pb-8')}>
              {/* Label + description */}
              <div className={cn(!isVertical && 'mt-2 text-center')}>
                <p
                  className={cn(
                    'text-sm font-medium',
                    status === 'active' && 'text-primary',
                    status === 'completed' && 'text-foreground',
                    status === 'upcoming' && 'text-muted-foreground'
                  )}
                >
                  {step.label}
                </p>
                {step.description && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{step.description}</p>
                )}
              </div>

              {/* Vertical connector */}
              {!isLast && isVertical && (
                <div
                  className={cn(
                    'ms-[15px] mt-2 w-0.5 h-6',
                    index < activeStep ? 'bg-primary' : 'bg-border'
                  )}
                  aria-hidden="true"
                />
              )}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

export interface StepperActionsProps {
  onNext: () => void;
  onBack: () => void;
  activeStep: number;
  totalSteps: number;
  nextLabel: string;
  backLabel: string;
  finishLabel: string;
  isNextDisabled?: boolean;
}

function StepperActions({
  onNext,
  onBack,
  activeStep,
  totalSteps,
  nextLabel,
  backLabel,
  finishLabel,
  isNextDisabled = false,
}: StepperActionsProps) {
  const isFirst = activeStep === 0;
  const isLast = activeStep === totalSteps - 1;

  return (
    <div className="flex items-center justify-between gap-3 pt-4">
      <Button
        variant="outline"
        onClick={onBack}
        disabled={isFirst}
      >
        {backLabel}
      </Button>
      <Button
        onClick={onNext}
        disabled={isNextDisabled}
      >
        {isLast ? finishLabel : nextLabel}
      </Button>
    </div>
  );
}

export { Stepper, StepperActions };
export default Stepper;
